import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHudLines, useFeeVersions } from '../../hooks/useFees';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';
import HudLines from './HudLines';
import { FeeHudLineViewModel, CreateOrUpdateFeeHudLineViewModel } from '../../models/fees';

const HudLinesContainer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  useEffect(() => {
    const versionIdParam = searchParams.get('versionId');
    if (versionIdParam) {
      const versionId = parseInt(versionIdParam, 10);
      if (!isNaN(versionId)) {
        setSelectedVersionId(versionId);
      }
    }
  }, [searchParams]);
  const [selectedHudLine, setSelectedHudLine] = useState<FeeHudLineViewModel | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const toastRef = React.useRef<Toast>(null);

  const { versionsQuery } = useFeeVersions();
  const { hudLinesQuery, createHudLine, updateHudLine, deleteHudLine, importHudLines } = useHudLines(selectedVersionId);

  const handleCreate = () => {
    setSelectedHudLine(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (hudLine: FeeHudLineViewModel) => {
    setSelectedHudLine(hudLine);
    setIsEditorOpen(true);
  };

  const handleSave = async (payload: CreateOrUpdateFeeHudLineViewModel) => {
    try {
      if (selectedHudLine) {
        // Update existing HUD line
        await updateHudLine({ id: selectedHudLine.feeHudLineId, payload });
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'HUD Line updated successfully',
          life: 3000,
        });
      } else {

        await createHudLine(payload);
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'HUD Line created successfully',
          life: 3000,
        });
      }
      setIsEditorOpen(false);
      setSelectedHudLine(null);
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to ${selectedHudLine ? 'update' : 'create'} HUD Line`,
        life: 3000,
      });
      console.error('Failed to save HUD Line:', error);
    }
  };

  const handleDelete = async (hudLineId: number) => {
    try {
      await deleteHudLine(hudLineId);
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'HUD Line deleted successfully',
        life: 3000,
      });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete HUD Line',
        life: 3000,
      });
      console.error('Failed to delete HUD Line:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditorOpen(false);
    setSelectedHudLine(null);
  };

  const handleImport = async (file: File) => {
    try {
      const result = await importHudLines(file);
      
      if (result.successfulRows > 0) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Import Completed',
          detail: `Successfully imported ${result.successfulRows} of ${result.totalRows} HUD lines`,
          life: 3000,
        });
      }
      
      return result;
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Import Failed',
        detail: error instanceof Error ? error.message : 'Failed to import HUD lines',
        life: 5000,
      });
      throw error;
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <ConfirmPopup />
      <HudLines
        hudLines={hudLinesQuery.data || []}
        versions={versionsQuery.data || []}
        loading={hudLinesQuery.isLoading || versionsQuery.isLoading}
        selectedVersionId={selectedVersionId}
        onVersionChange={setSelectedVersionId}
        onCreateClick={handleCreate}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        onImport={handleImport}
        onBackClick={() => navigate(selectedVersionId ? `/fees?versionId=${selectedVersionId}` : '/fees')}
        isEditorOpen={isEditorOpen}
        selectedHudLine={selectedHudLine}
        onSave={handleSave}
        onCancelEdit={handleCancelEdit}
      />
    </>
  );
};

export default HudLinesContainer;

